import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FeaturesService } from 'src/app/shared/services/features.service';
import { NotificationsDataService } from 'src/app/notifications/data-access/notifications-data.service';
import { ManagePdfReportsDataService } from 'src/app/reports/data-access/manage-pdf-reports-data.service';
import { FeedbackDialogComponent } from '../feedback-dialog.component';
import { CreateProjectWatchDialogComponent } from './create-project-watch-dialog/create-project-watch-dialog.component';
import { SingleProjectPdfReportDialogComponent } from './single-project-pdf-report-dialog/single-project-pdf-report-dialog.component';
import { ProjectAction, ProjectActionsProject } from './project-actions.types';
import { urlEncode } from 'src/app/util/util';

// Mirrors the legacy AngularJS "actionMenu" from src/app-ds3/search/projectDisplay.js
@Component({
    selector: 'eic-project-actions',
    templateUrl: './project-actions.component.html',
    styleUrl: './project-actions.component.scss'
})
export class ProjectActionsComponent {
    @Input() project!: ProjectActionsProject;

    // Cache keyed on input identity so *ngFor doesn't get a new array/objects every CD cycle (breaks click handling)
    private cachedProject: ProjectActionsProject | undefined;
    private cachedActions: ProjectAction[] = [];

    constructor(
        private dialog: MatDialog,
        private features: FeaturesService,
        private notificationsDataService: NotificationsDataService,
        private managePdfReportsDataService: ManagePdfReportsDataService
    ) { }

    get actions(): ProjectAction[] {
        if (this.project !== this.cachedProject) {
            this.cachedProject = this.project;
            this.cachedActions = this.buildActions();
        }

        return this.cachedActions;
    }

    get menuLabel(): string {
        return this.project?.discriminator === 'Asset' ? 'Asset Actions' : 'Project Actions';
    }

    private buildActions(): ProjectAction[] {
        if (!this.project) return [];

        // Assets only support share and feedback for now
        if (this.project.discriminator === 'Asset') {
            return [this.shareAction(), this.feedbackAction()];
        }

        return [
            this.shareAction(),
            this.watchAction(),
            this.pdfReportAction(),
            //this.printAction(),
            this.feedbackAction()
        ];
    }

    actionVisible(action: ProjectAction): boolean {
        return action.visible ? action.visible() : true;
    }

    actionDisabled(action: ProjectAction): boolean {
        return action.disabled ? action.disabled() : false;
    }

    runAction(action: ProjectAction, event?: Event): void {
        event?.stopPropagation();

        if (this.actionDisabled(action)) return;

        action.onClick();
    }

    private shareAction(): ProjectAction {
        return {
            id: 'share',
            label: 'Share...',
            onClick: () => {
                window.location.href = this.buildShareMailtoHref();
            }
        };
    }

    // Exposed for testing without triggering an actual navigation via window.location.href
    buildShareMailtoHref(): string {
        const productName = this.features.product?.fullName || 'EICDataStream';
        const itemType = this.project.discriminator === 'Asset' ? 'asset' : 'project';

        // Share must link to the legacy /search/project route, not the current (new-app) URL, for backward-compatible bookmarking
        const legacyProjectPath =
            `${window.location.origin}/search/project/${this.project.id}/${urlEncode(this.project.name)}`;
        return `mailto:?subject=Information from ${productName}&body=I thought you might be interested in this ${itemType} from ${productName}:%0D%0A%0D%0A${legacyProjectPath}%0D%0A%0D%0A`;
    }

    private watchAction(): ProjectAction {
        return {
            id: 'watch',
            label: 'Watch this project...',
            onClick: () => this.dialog.open(CreateProjectWatchDialogComponent, {
                width: '500px',
                data: { projectId: this.project.id, projectName: this.project.name }
            })
        };
    }

    private pdfReportAction(): ProjectAction {
        const suspended = !!this.features.legacySwitches?.NEW_REPORT_REQUEST?.SUSPENDED;

        return {
            id: 'pdfReport',
            label: suspended ? 'PDF Report Service Suspended' : 'PDF report...',
            disabled: () => suspended,
            onClick: () => this.dialog.open(SingleProjectPdfReportDialogComponent, {
                width: '550px',
                data: { projectId: this.project.id, projectName: this.project.name }
            })
        };
    }

    private printAction(): ProjectAction {
        return {
            id: 'print',
            label: 'Print...',
            onClick: () => window.print()
        };
    }

    private feedbackAction(): ProjectAction {
        return {
            id: 'feedback',
            label: `Ask/tell us about this ${this.project.discriminator.toLowerCase()}...`,
            visible: () => !!this.features.legacySwitches?.MEMBER_FEEDBACK?.EnableProjectFeedback,
            onClick: () => this.dialog.open(FeedbackDialogComponent, {
                width: '90vw',
                maxWidth: '600px',
                height: 'auto',
                maxHeight: '90vh',
                data: this.project
            })
        };
    }
}
